import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import type { SxProps, Theme } from "@mui/material/styles";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  GoodUnits,
  ShipmentLocationType,
  useGetOrganizationSettingsQuery,
} from "../../../graphql/generated";
import { ShipmentView } from "../../../utils/shipment/shipmentView";
import InfoBlock from "../../common/InfoBlock";
import goodUnitLabel from "../../../utils/labels/goodUnitsLabel";
import { formatLoadedQuantity } from "../../../utils/formatLoadedQuantity";
import { formatDeliveredQuantity } from "../../../utils/formatDeliveredQuantity";
import { useTranslation } from "react-i18next";
import { testIds } from "./ShipmentDetails.testIds";

type TankRow = {
  storageFacilityId: string;
  tankIdentifier: string;
  receiverName: string;
  ordered: number;
  delivered: number | null;
  inchesBefore: number | null;
  inchesAfter: number | null;
};

type CommodityRow = {
  goodProfileId: string;
  label: string;
  unit: GoodUnits;
  supplierName: string | null;
  totalOrdered: number;
  totalLoaded: number | null;
  totalLoadedNet: number | null;
  totalDelivered: number | null;
  totalDeliveredNet: number | null;
  tanks: TankRow[];
};

export function buildCommodityRows(
  shipment: ShipmentView
): { rows: CommodityRow[]; isTankBased: boolean } {
  const pickupLocations = shipment.shipmentLocations.filter(
    (loc) => loc.locationType === ShipmentLocationType.Pickup
  );
  const dropOffLocations = shipment.shipmentLocations.filter(
    (loc) => loc.locationType === ShipmentLocationType.DropOff
  );

  const isTankBased = dropOffLocations.some((loc) =>
    (loc.receivedGoods || []).some((rg) => rg.storageFacilityId)
  );

  const map = new Map<string, CommodityRow>();

  for (const loc of pickupLocations) {
    for (const sg of loc.shippedGoods || []) {
      if (!sg.goodProfileId) continue;
      const key = String(sg.goodProfileId);
      if (!map.has(key)) {
        map.set(key, {
          goodProfileId: key,
          label: sg.label,
          unit: sg.unit || GoodUnits.Item,
          supplierName: sg.supplier?.name ?? null,
          totalOrdered: 0,
          totalLoaded: null,
          totalLoadedNet: null,
          totalDelivered: null,
          totalDeliveredNet: null,
          tanks: [],
        });
      }
      const row = map.get(key)!;
      row.totalOrdered += sg.quantity;
      if (sg.loadedQuantity != null) {
        row.totalLoaded = (row.totalLoaded ?? 0) + sg.loadedQuantity;
      }
      if (sg.netLoadedQuantity != null) {
        row.totalLoadedNet = (row.totalLoadedNet ?? 0) + sg.netLoadedQuantity;
      }
    }
  }

  for (const loc of dropOffLocations) {
    for (const rg of loc.receivedGoods || []) {
      if (!rg.goodProfileId) continue;
      const key = String(rg.goodProfileId);
      const row = map.get(key);
      if (!row) continue;

      if (rg.deliveredQuantity != null) {
        row.totalDelivered = (row.totalDelivered ?? 0) + rg.deliveredQuantity;
      }
      if (rg.netDeliveredQuantity != null) {
        row.totalDeliveredNet =
          (row.totalDeliveredNet ?? 0) + rg.netDeliveredQuantity;
      }

      if (isTankBased && rg.storageFacilityId) {
        const sf = (loc.receiver?.storageFacilities || []).find(
          (f) => f._id === rg.storageFacilityId
        );
        const tankIdentifier = sf?.identifier
          ? sf.identifier
          : `Tank #${rg.storageFacilityId.slice(0, 6)}`;

        row.tanks.push({
          storageFacilityId: rg.storageFacilityId,
          tankIdentifier,
          receiverName: loc.receiver?.name ?? "",
          ordered: rg.quantity ?? 0,
          delivered: rg.deliveredQuantity ?? null,
          inchesBefore: rg.inchesBefore ?? null,
          inchesAfter: rg.inchesAfter ?? null,
        });
      }
    }
  }

  return { rows: Array.from(map.values()), isTankBased };
}

type Props = {
  shipment: ShipmentView;
};

const tableHeaderCellSx: SxProps<Theme> = {
  fontSize: 14,
  fontWeight: 700,
  py: 1.25,
  whiteSpace: "nowrap",
};

const toggleCellSx: SxProps<Theme> = {
  width: 56,
  minWidth: 56,
  maxWidth: 56,
  px: 1.25,
};

const commodityCellSx: SxProps<Theme> = {
  minWidth: 260,
  py: 1.5,
  whiteSpace: "normal",
  overflow: "visible",
};

const metricCellSx: SxProps<Theme> = {
  minWidth: 140,
  py: 1.5,
};

const commodityTableSx: SxProps<Theme> = {
  minWidth: 760,
  borderCollapse: "collapse",
  [`&& .${tableCellClasses.root}`]: {
    borderLeft: "0 !important",
    borderRight: "0 !important",
    borderRadius: "0 !important",
  },
  [`&& .${tableCellClasses.body}`]: {
    borderTop: (theme) =>
      `1px solid ${theme.palette.primaryBackground.dark} !important`,
    borderBottom: (theme) =>
      `1px solid ${theme.palette.primaryBackground.dark} !important`,
  },
  [`&& .${tableCellClasses.body}:first-of-type`]: {
    borderLeft: "0 !important",
  },
  [`&& .${tableCellClasses.body}:last-child`]: {
    borderRight: "0 !important",
  },
};

const detailContainerSx: SxProps<Theme> = {
  backgroundColor: (theme) => theme.palette.primaryBackground.main,
  borderTop: (theme) => `1px solid ${theme.palette.primaryBackground.dark}`,
  borderBottom: (theme) => `1px solid ${theme.palette.primaryBackground.dark}`,
  borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
};

const detailTableSx: SxProps<Theme> = {
  width: "100%",
  tableLayout: "fixed",
  minWidth: 900,
  borderCollapse: "collapse",
  [`&& .${tableCellClasses.root}`]: {
    borderLeft: "0 !important",
    borderRight: "0 !important",
    borderRadius: "0 !important",
  },
  [`&& .${tableCellClasses.head}`]: {
    backgroundColor: (theme) =>
      `${theme.palette.primaryBackground.dark} !important`,
    color: (theme) => theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    py: 0.75,
  },
  [`&& .${tableCellClasses.body}`]: {
    backgroundColor: "transparent",
    borderTop: (theme) =>
      `1px solid ${theme.palette.primaryBackground.dark} !important`,
    borderBottom: (theme) =>
      `1px solid ${theme.palette.primaryBackground.dark} !important`,
  },
  [`&& .${tableCellClasses.body}:first-of-type`]: {
    borderLeft: "0 !important",
  },
  [`&& .${tableCellClasses.body}:last-child`]: {
    borderRight: "0 !important",
  },
  [`&& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .${tableCellClasses.body}`]:
    {
      backgroundColor: (theme) =>
        `${theme.palette.primaryBackground.dark} !important`,
    },
};

const detailSpacerCellSx: SxProps<Theme> = {
  width: 56,
  minWidth: 56,
  maxWidth: 56,
  p: 0,
};

const CommodityDetailsTable = ({ shipment }: Props): JSX.Element | null => {
  const { t } = useTranslation(["orders", "common"]);
  const orgSettings = useGetOrganizationSettingsQuery();
  const netEnabled =
    !!orgSettings.data?.organizationSettings?.dispatching
      ?.trackNetAndGrossVolumes;
  const { rows, isTankBased } = useMemo(
    () => buildCommodityRows(shipment),
    [shipment]
  );

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  if (!rows.length) return null;

  // expand + Commodity + Ordered + Loaded + Delivered
  // (+2 when Net is enabled: Loaded/Delivered each split into Gross + Net)
  const colCount = netEnabled ? 7 : 5;

  return (
    <InfoBlock
      dataTestId={testIds.shipmentDetailsLiquidCommodities}
      title={t("liquidCommodityDetails", "Liquid Commodity Details")}
      childrenContainerSx={{ p: 1 }}
    >
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          aria-label="Liquid commodity details"
          sx={commodityTableSx}
        >
          <TableHead>
            <TableRow>
              {isTankBased && <TableCell sx={toggleCellSx} />}
              <TableCell sx={tableHeaderCellSx}>{t("commodity", "Commodity")}</TableCell>
              <TableCell align="right" sx={tableHeaderCellSx}>
                {t("ordered", "Ordered")}
              </TableCell>
              {netEnabled ? (
                <>
                  <TableCell align="right" sx={tableHeaderCellSx}>
                    {t("loaded", "Loaded")} ({t("common:gross", "Gross")})
                  </TableCell>
                  <TableCell align="right" sx={tableHeaderCellSx}>
                    {t("loaded", "Loaded")} ({t("common:net", "Net")})
                  </TableCell>
                  <TableCell align="right" sx={tableHeaderCellSx}>
                    {t("delivered", "Delivered")} ({t("common:gross", "Gross")})
                  </TableCell>
                  <TableCell align="right" sx={tableHeaderCellSx}>
                    {t("delivered", "Delivered")} ({t("common:net", "Net")})
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell align="right" sx={tableHeaderCellSx}>
                    {t("loaded", "Loaded")}
                  </TableCell>
                  <TableCell align="right" sx={tableHeaderCellSx}>
                    {t("delivered", "Delivered")}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isOpen = expanded.has(row.goodProfileId);
              const detailsId = `commodity-details-${row.goodProfileId}`;
              return (
                <React.Fragment key={row.goodProfileId}>
                  <TableRow
                    data-testid={`commodity-row-${row.goodProfileId}`}
                    data-commodity-name={row.label}
                    hover={row.tanks.length > 0}
                    sx={{
                      backgroundColor: isOpen
                        ? "rgba(97, 0, 16, 0.04)"
                        : "transparent",
                      "& > .MuiTableCell-root": {
                        borderBottom: isOpen ? "0 !important" : undefined,
                      },
                    }}
                  >
                    {isTankBased && (
                      <TableCell padding="checkbox" sx={toggleCellSx}>
                        {row.tanks.length > 0 && (
                          <IconButton
                            size="small"
                            color="primary"
                            data-testid={`expand-${row.goodProfileId}`}
                            aria-label={
                              isOpen
                                ? `Collapse tank details for ${row.label}`
                                : `Expand tank details for ${row.label}`
                            }
                            aria-expanded={isOpen}
                            aria-controls={detailsId}
                            onClick={() => toggle(row.goodProfileId)}
                          >
                            {isOpen ? (
                              <KeyboardArrowUp fontSize="small" />
                            ) : (
                              <KeyboardArrowDown fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                    <TableCell component="th" scope="row" sx={commodityCellSx}>
                      <Typography
                        data-testid={`commodity-label-${row.goodProfileId}`}
                        variant="body2"
                        fontWeight="medium"
                      >
                        {row.label}
                      </Typography>
                      {row.supplierName && (
                        <Typography
                          data-testid={`commodity-supplier-${row.goodProfileId}`}
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {row.supplierName}
                        </Typography>
                      )}
                      <Typography
                        data-testid={`commodity-unit-${row.goodProfileId}`}
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {goodUnitLabel(row.unit)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      data-testid={`commodity-ordered-${row.goodProfileId}`}
                      align="right"
                      sx={metricCellSx}
                    >
                      {row.totalOrdered}
                    </TableCell>
                    {netEnabled ? (
                      <>
                        <TableCell
                          data-testid={`commodity-loaded-${row.goodProfileId}`}
                          align="right"
                          sx={metricCellSx}
                        >
                          {formatLoadedQuantity(row.totalLoaded)}
                        </TableCell>
                        <TableCell
                          data-testid={`commodity-loaded-net-${row.goodProfileId}`}
                          align="right"
                          sx={metricCellSx}
                        >
                          {formatLoadedQuantity(row.totalLoadedNet)}
                        </TableCell>
                        <TableCell
                          data-testid={`commodity-delivered-${row.goodProfileId}`}
                          align="right"
                          sx={metricCellSx}
                        >
                          {formatDeliveredQuantity(row.totalDelivered)}
                        </TableCell>
                        <TableCell
                          data-testid={`commodity-delivered-net-${row.goodProfileId}`}
                          align="right"
                          sx={metricCellSx}
                        >
                          {formatDeliveredQuantity(row.totalDeliveredNet)}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell
                          data-testid={`commodity-loaded-${row.goodProfileId}`}
                          align="right"
                          sx={metricCellSx}
                        >
                          {formatLoadedQuantity(row.totalLoaded)}
                        </TableCell>
                        <TableCell
                          data-testid={`commodity-delivered-${row.goodProfileId}`}
                          align="right"
                          sx={metricCellSx}
                        >
                          {formatDeliveredQuantity(row.totalDelivered)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>

                  {isTankBased && row.tanks.length > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={colCount}
                        sx={{
                          p: "0 !important",
                          borderTop: "0 !important",
                          borderBottom: isOpen ? undefined : "0 !important",
                        }}
                      >
                        <Collapse
                          in={isOpen}
                          timeout="auto"
                          unmountOnExit
                          id={detailsId}
                        >
                          <Box sx={detailContainerSx}>
                            <TableContainer sx={{ overflowX: "auto" }}>
                              <Table
                                size="small"
                                aria-label={`${row.label} tank details`}
                                sx={detailTableSx}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      aria-hidden="true"
                                      sx={detailSpacerCellSx}
                                    />
                                    <TableCell sx={{ width: "16%" }}>
                                      {t("tank", "Tank")}
                                    </TableCell>
                                    <TableCell sx={{ width: "28%" }}>
                                      {t("receiver", "Receiver")}
                                    </TableCell>
                                    <TableCell align="right">{t("ordered", "Ordered")}</TableCell>
                                    <TableCell align="right">
                                      {t("delivered", "Delivered")}
                                    </TableCell>
                                    <TableCell align="right">
                                      {t("inchesBefore", "Inches Before")}
                                    </TableCell>
                                    <TableCell align="right">
                                      {t("inchesAfter", "Inches After")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {row.tanks.map((tank, i) => {
                                    const prevTank =
                                      i > 0 ? row.tanks[i - 1] : null;
                                    const showReceiver =
                                      !prevTank ||
                                      prevTank.receiverName !==
                                        tank.receiverName;
                                    return (
                                      <TableRow
                                        data-testid={`tank-row-${
                                          row.goodProfileId
                                        }-${
                                          tank.storageFacilityId ||
                                          `${tank.tankIdentifier}-${tank.receiverName}`
                                        }`}
                                        key={`${tank.tankIdentifier}-${tank.receiverName}-${i}`}
                                      >
                                        <TableCell
                                          aria-hidden="true"
                                          sx={detailSpacerCellSx}
                                        />
                                        <TableCell
                                          data-testid={`tank-identifier-${
                                            row.goodProfileId
                                          }-${
                                            tank.storageFacilityId ||
                                            `${tank.tankIdentifier}-${tank.receiverName}`
                                          }`}
                                          component="th"
                                          scope="row"
                                        >
                                          {tank.tankIdentifier}
                                        </TableCell>
                                        <TableCell data-testid={`tank-receiver-${
                                            row.goodProfileId
                                          }-${
                                            tank.storageFacilityId ||
                                            `${tank.tankIdentifier}-${tank.receiverName}`
                                          }`}>
                                          {showReceiver
                                            ? tank.receiverName
                                            : ""}
                                        </TableCell>
                                        <TableCell
                                          data-testid={`tank-ordered-${
                                            row.goodProfileId
                                          }-${
                                            tank.storageFacilityId ||
                                            `${tank.tankIdentifier}-${tank.receiverName}`
                                          }`}
                                          align="right"
                                        >
                                          {tank.ordered}
                                        </TableCell>
                                        <TableCell
                                          data-testid={`tank-delivered-${
                                            row.goodProfileId
                                          }-${
                                            tank.storageFacilityId ||
                                            `${tank.tankIdentifier}-${tank.receiverName}`
                                          }`}
                                          align="right"
                                        >
                                          {formatDeliveredQuantity(
                                            tank.delivered
                                          )}
                                        </TableCell>
                                        <TableCell
                                          data-testid={`tank-inches-before-${
                                            row.goodProfileId
                                          }-${
                                            tank.storageFacilityId ||
                                            `${tank.tankIdentifier}-${tank.receiverName}`
                                          }`}
                                          align="right"
                                        >
                                          {formatDeliveredQuantity(
                                            tank.inchesBefore
                                          )}
                                        </TableCell>
                                        <TableCell
                                          data-testid={`tank-inches-after-${
                                            row.goodProfileId
                                          }-${
                                            tank.storageFacilityId ||
                                            `${tank.tankIdentifier}-${tank.receiverName}`
                                          }`}
                                          align="right"
                                        >
                                          {formatDeliveredQuantity(
                                            tank.inchesAfter
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </InfoBlock>
  );
};

export default CommodityDetailsTable;
